import React, { useState } from 'react';

import CardDialog from '~/components/CardDialog';
import { Caption, FsButton, Heading, LoadingSwitch, Text } from '~/components/core';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import { PencilIcon } from '~/components/icons';
import Switch from '~/components/ui/atoms/Switch';
import cn from '~/Utils/cn';

import { useGbgConfiguration } from './hooks/useGbgConfiguration';
import GbgSetupDialog from './GbgSetupDialog';
import TEXTS from './texts';
import type { GbgConfigurationFormValues } from './types';

interface DetailProps {
  label: string;
  text: string;
}

const Detail = ({ label, text }: DetailProps) => (
  <div className="mr-40 min-w-40">
    <Caption variant={Caption.VARIANTS.LABEL}>{label}</Caption>
    <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
      {text}
    </Text>
  </div>
);

const InternalAdminGbg = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { isLoading, isError, configuration, hasConfiguration, saveConfiguration, enableConfiguration, isSaving } =
    useGbgConfiguration();

  const containerWithBorderCn = 'border border-solid border-slate-600 rounded-md p-24 mt-20 w-full';
  const sectionTitleCn = 'flex items-center';
  const sectionDetailsCn = 'mt-12 flex';

  const handleInitialEnableConfiguration = async () => {
    await enableConfiguration();
    setDialogOpen(true);
  };

  const handleSubmit = async (values: GbgConfigurationFormValues) => {
    await saveConfiguration(values);
    setDialogOpen(false);
  };

  const toggleEnabled = async (shouldEnable: boolean) => {
    await enableConfiguration(shouldEnable);
  };

  return (
    <LoadingSwitch isLoading={isLoading} isError={isError}>
      <CardDialog noCardTitle>
        {hasConfiguration && configuration ? (
          <div className="flex flex-col gap-12">
            <div className={cn({ 'opacity-30': !configuration.is_enabled })}>
              <div className={containerWithBorderCn}>
                <div className={cn(sectionTitleCn, '-mt-8')}>
                  <Heading variant={Heading.TYPES.H4}>{TEXTS.gbgConnection}</Heading>
                  <IconButton onClick={() => setDialogOpen(true)} disabled={!configuration.is_enabled || isSaving}>
                    <PencilIcon />
                  </IconButton>
                </div>
                <div className={sectionDetailsCn}>
                  <Detail label={TEXTS.apiBaseUrl} text={configuration.api_base_url || ''} />
                  <Detail label={TEXTS.apiUsername} text={configuration.api_username || ''} />
                  <Detail label={TEXTS.apiToken} text={configuration.api_token_output || ''} />
                </div>
              </div>
              <div className={containerWithBorderCn}>
                <div className={sectionTitleCn}>
                  <Heading variant={Heading.TYPES.H4}>{TEXTS.features}</Heading>
                </div>
                <div className={sectionDetailsCn}>
                  <Detail
                    label={TEXTS.addressValidation}
                    text={configuration.is_address_validation_enabled ? TEXTS.enabled : TEXTS.disabled}
                  />
                  <Detail
                    label={TEXTS.businessValidation}
                    text={configuration.is_business_validation_enabled ? TEXTS.enabled : TEXTS.disabled}
                  />
                </div>
              </div>
            </div>
            <label className="flex items-center gap-4">
              <Switch
                checked={configuration.is_enabled}
                onChange={() => toggleEnabled(!configuration.is_enabled)}
                disabled={isSaving}
              />
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM}>
                {TEXTS.gbgIntegrationEnabled}
              </Text>
            </label>
            <div>
              <Caption variant={Caption.VARIANTS.LABEL}>
                {TEXTS.lastUpdatedBy(configuration.last_updated_by_username)}
              </Caption>
            </div>
          </div>
        ) : (
          <FsButton
            color="primary"
            variant="contained"
            className="mb-40 mt-40"
            onClick={handleInitialEnableConfiguration}
            disabled={isSaving}
          >
            {TEXTS.enableGbgIntegration}
          </FsButton>
        )}
      </CardDialog>
      {dialogOpen ? (
        <GbgSetupDialog
          onSubmit={handleSubmit}
          onClose={() => setDialogOpen(false)}
          isEdit={hasConfiguration}
          initialValues={
            hasConfiguration && configuration
              ? {
                  api_base_url: configuration.api_base_url,
                  api_username: configuration.api_username,
                  is_address_validation_enabled: configuration.is_address_validation_enabled,
                  is_business_validation_enabled: configuration.is_business_validation_enabled,
                }
              : undefined
          }
        />
      ) : null}
    </LoadingSwitch>
  );
};

export default InternalAdminGbg;
